import React, { useEffect, useState, useCallback } from "react";
import { Loader2, LogOut, Download, Users, Target, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { adminLogin, adminGetData, adminExportUrl } from "@/lib/api";

const TOKEN_KEY = "sf_admin_token";

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await adminLogin(email.trim(), password);
      localStorage.setItem(TOKEN_KEY, token);
      onLogin(token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-sf-bg border border-sf-border rounded-sm px-4 py-3 text-sf-bone placeholder:text-sf-muted font-body text-sm focus:outline-none focus:border-sf-ember transition-colors";
  const labelCls = "font-mono text-[11px] uppercase tracking-[0.2em] text-sf-dim mb-1.5 block";

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-sf-surface border border-sf-border rounded-md p-8"
      >
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={16} className="text-sf-ember" />
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-sf-ember">Command Access</span>
        </div>
        <h1 className="sf-stencil text-2xl text-sf-bone tracking-wide mb-6">Sacred Frontline · Admin</h1>
        <div className="space-y-4">
          <div>
            <label className={labelCls} htmlFor="ad-email">Email</label>
            <input id="ad-email" type="email" data-testid="admin-email-input" className={field} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin email" />
          </div>
          <div>
            <label className={labelCls} htmlFor="ad-pass">Password</label>
            <input id="ad-pass" type="password" data-testid="admin-password-input" className={field} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="password" />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full bg-gradient-to-r from-sf-emberDeep to-sf-emberBright text-white font-stencil uppercase tracking-[0.15em] text-sm px-6 py-3.5 rounded-sm disabled:opacity-70 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="animate-spin" size={16} /> Verifying…</> : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="bg-sf-surface border border-sf-border rounded-md p-6">
      <div className="flex items-center gap-2.5 text-sf-ember mb-3">{icon}
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-sf-dim">{label}</span>
      </div>
      <div className="sf-stencil text-4xl text-sf-bone">{value}</div>
      {sub && <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-sf-muted">{sub}</div>}
    </div>
  );
}

function DataTable({ title, rows, columns, exportKind, token }) {
  const download = async () => {
    try {
      const res = await fetch(adminExportUrl(exportKind), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sacred_frontline_${exportKind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Couldn't download CSV.");
    }
  };

  return (
    <div className="bg-sf-surface border border-sf-border rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-sf-border">
        <h2 className="sf-stencil text-lg tracking-wide text-sf-bone">{title} <span className="text-sf-muted">({rows.length})</span></h2>
        <button
          onClick={download}
          data-testid={`admin-export-${exportKind}`}
          className="flex items-center gap-2 border border-sf-border text-sf-bone hover:border-sf-ember hover:bg-sf-ember/5 font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2 rounded-sm transition-colors"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-sf-border">
              {columns.map((c) => (
                <th key={c.key} className="px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-sf-muted">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="px-6 py-8 text-center text-sm text-sf-muted font-mono uppercase tracking-[0.15em]">No records yet</td></tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i} className="border-b border-sf-border/50 hover:bg-sf-surfaceHover">
                  {columns.map((c) => (
                    <td key={c.key} className="px-6 py-3 text-sm text-sf-dim">{c.render ? c.render(r[c.key]) : (r[c.key] || "—")}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const AdminDashboard = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const d = await adminGetData(t);
      setData(d);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Session expired — sign in again.");
      } else {
        toast.error("Couldn't load data.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setData(null);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="sf-grain min-h-screen bg-sf-bg text-sf-bone">
      {!token ? (
        <LoginForm onLogin={setToken} />
      ) : (
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
          <div className="flex items-center justify-between mb-10">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-sf-ember mb-1">Command Dashboard</div>
              <h1 className="sf-stencil text-3xl text-sf-bone tracking-wide">Sacred Frontline</h1>
            </div>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 border border-sf-border text-sf-dim hover:text-sf-bone hover:border-sf-ember font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2 rounded-sm transition-colors"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>

          {loading && !data ? (
            <div className="flex items-center gap-2 text-sf-dim"><Loader2 className="animate-spin" size={18} /> Loading…</div>
          ) : data ? (
            <>
              <div className="grid sm:grid-cols-3 gap-5 mb-10">
                <StatCard
                  icon={<Target size={16} />}
                  label="Reservations"
                  value={data.counts.reservations}
                  sub={`Goal ${data.counts.goal} · ${Math.round((data.counts.reservations / data.counts.goal) * 100)}%`}
                />
                <StatCard icon={<Users size={16} />} label="Launch List" value={data.counts.subscribers} sub="Total emails collected" />
                <StatCard icon={<Target size={16} />} label="To Goal" value={Math.max(0, data.counts.goal - data.counts.reservations)} sub="Reservations remaining" />
              </div>

              <div className="space-y-8">
                <DataTable
                  title="Reservations"
                  rows={data.reservations}
                  exportKind="reservations"
                  token={token}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "created_at", label: "Reserved", render: fmtDate },
                  ]}
                />
                <DataTable
                  title="Launch Updates List"
                  rows={data.subscribers}
                  exportKind="subscribers"
                  token={token}
                  columns={[
                    { key: "email", label: "Email" },
                    { key: "source", label: "Source" },
                    { key: "created_at", label: "Joined", render: fmtDate },
                  ]}
                />
              </div>
            </>
          ) : null}
        </div>
      )}
      <Toaster position="top-center" theme="dark" />
    </div>
  );
};

export default AdminDashboard;
